import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Settings, Plus, Pencil, Trash2, Loader2, AlertTriangle, Users, Search, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "../../components/ui/alert-dialog";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { toast } from "sonner";

interface Organisation {
  id: number;
  name: string;
  companyName?: string;
  abn: string | null;
  address: string | null;
}

interface OfferTemplate {
  id: number;
  name: string;
  subject: string;
  isDefault: boolean;
  isActive: boolean;
  isSystem: boolean;
  templateType: string | null;
  customFields: { label: string; type: string; required: boolean }[];
  createdAt: string;
}

const RecruitmentSettings: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  if (!ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_SETTINGS)) {
    return <AccessRestricted />;
  }
  const [activeTab, setActiveTab] = useState("organisations");
  const [templates, setTemplates] = useState<OfferTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  // Module Access state
  interface ModuleAccessItem {
    user: {
      id: number;
      name: string;
      email: string;
      userRole: string;
    };
    accessLevel: string;
    isConfigurable: boolean;
  }
  const [moduleAccess, setModuleAccess] = useState<ModuleAccessItem[]>([]);
  const [moduleAccessLoading, setModuleAccessLoading] = useState(false);
  const [updatingAccessUserId, setUpdatingAccessUserId] = useState<number | null>(null);
  const canManageModuleAccess = ability.can(ACTIONS.UPDATE, RESOURCES.RECRUITMENT_SETTINGS);
  const [moduleAccessSearch, setModuleAccessSearch] = useState("");
  const [moduleAccessPage, setModuleAccessPage] = useState(1);
  const [moduleAccessLimit] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<OfferTemplate | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Organisations state
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [organisationsLoading, setOrganisationsLoading] = useState(false);
  const [showOrgDialog, setShowOrgDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organisation | null>(null);
  const [orgName, setOrgName] = useState("");
  const [orgAbn, setOrgAbn] = useState("");
  const [orgAddress, setOrgAddress] = useState("");
  const [orgSubmitting, setOrgSubmitting] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<Organisation | null>(null);
  const [orgDeleteLoading, setOrgDeleteLoading] = useState(false);

  useEffect(() => {
    if (activeTab === "organisations") {
      fetchOrganisations();
    }
    if (activeTab === "offer-templates") {
      fetchTemplates();
    }
    if (activeTab === "module-access") {
      fetchModuleAccess();
    }
  }, [activeTab]);

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await api.get<{ success: boolean; templates: OfferTemplate[] }>("/api/offer-templates");
      if (res.success) setTemplates(res.templates);
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const fetchOrganisations = async () => {
    setOrganisationsLoading(true);
    try {
      const res = await api.get<{ success: boolean; organizations: Organisation[] }>("/api/requisition-organizations");
      if (res.success) setOrganisations(res.organizations);
    } catch (err) {
      console.error("Failed to load organisations:", err);
    } finally {
      setOrganisationsLoading(false);
    }
  };

  const openAddOrgDialog = () => {
    setEditingOrg(null);
    setOrgName("");
    setOrgAbn("");
    setOrgAddress("");
    setShowOrgDialog(true);
  };

  const openEditOrgDialog = (org: Organisation) => {
    setEditingOrg(org);
    setOrgName(org.name);
    setOrgAbn(org.abn || "");
    setOrgAddress(org.address || "");
    setShowOrgDialog(true);
  };

  const handleSaveOrganisation = async () => {
    if (!orgName.trim()) {
      toast.error("Organisation name is required");
      return;
    }
    if (!orgAbn.trim()) {
      toast.error("ABN number is required");
      return;
    }
    if (!orgAddress.trim()) {
      toast.error("Address is required");
      return;
    }
    setOrgSubmitting(true);
    try {
      if (editingOrg) {
        const res = await api.put<{ success: boolean; organization: Organisation }>(`/api/requisition-organizations/${editingOrg.id}`, {
          name: orgName.trim(),
          abn: orgAbn.trim(),
          address: orgAddress.trim(),
        });
        if (res.success) {
          toast.success("Organisation updated");
          setOrganisations((prev) =>
            prev.map((o) => (o.id === editingOrg.id ? res.organization : o))
          );
          setShowOrgDialog(false);
        }
      } else {
        const res = await api.post<{ success: boolean; organization: Organisation }>("/api/requisition-organizations", {
          name: orgName.trim(),
          abn: orgAbn.trim(),
          address: orgAddress.trim(),
        });
        if (res.success) {
          toast.success("Organisation created");
          setOrganisations((prev) => [...prev, res.organization]);
          setShowOrgDialog(false);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save organisation");
    } finally {
      setOrgSubmitting(false);
    }
  };

  const handleDeleteOrg = async () => {
    if (!orgToDelete) return;
    setOrgDeleteLoading(true);
    try {
      await api.delete(`/api/requisition-organizations/${orgToDelete.id}`);
      setOrganisations((prev) => prev.filter((o) => o.id !== orgToDelete.id));
      toast.success("Organisation deleted");
      setDeleteModalOpen(false);
      setOrgToDelete(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to delete organisation");
    } finally {
      setOrgDeleteLoading(false);
    }
  };

  const fetchModuleAccess = async () => {
    setModuleAccessLoading(true);
    try {
      const res = await api.get<{ success: boolean; moduleAccess: ModuleAccessItem[] }>("/api/requisitions/module-access");
      if (res.success) setModuleAccess(res.moduleAccess);
    } catch (err) {
      console.error("Failed to load module access:", err);
      toast.error("Failed to load module access settings");
    } finally {
      setModuleAccessLoading(false);
    }
  };

  const updateModuleAccess = async (userId: number, accessLevel: string) => {
    if (!canManageModuleAccess) return;
    setUpdatingAccessUserId(userId);
    try {
      const res = await api.post<{ success: boolean }>("/api/requisitions/module-access", {
        userId,
        accessLevel,
      });
      if (res.success) {
        toast.success("Module access updated");
        setModuleAccess((prev) =>
          prev.map((item) =>
            item.user.id === userId ? { ...item, accessLevel } : item
          )
        );
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update module access");
    } finally {
      setUpdatingAccessUserId(null);
    }
  };

  const openDeleteModal = (template: OfferTemplate) => {
    setTemplateToDelete(template);
    setDeleteModalOpen(true);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/api/offer-templates/${templateToDelete.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== templateToDelete.id));
      toast.success("Template deleted");
      setDeleteModalOpen(false);
      setTemplateToDelete(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to delete template");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Module Access — client-side filter + pagination
  // Module Access — client-side filter + pagination
  const systemTemplates = useMemo(() => templates.filter((t) => t.isSystem), [templates]);
  const customTemplates = useMemo(() => templates.filter((t) => !t.isSystem), [templates]);

  const filteredModuleAccess = useMemo(() => {
    const q = moduleAccessSearch.trim().toLowerCase();
    if (!q) return moduleAccess;
    return moduleAccess.filter(
      (item) =>
        item.user.name.toLowerCase().includes(q) ||
        item.user.email.toLowerCase().includes(q)
    );
  }, [moduleAccessSearch, moduleAccess]);

  const moduleAccessTotal = filteredModuleAccess.length;
  const moduleAccessPages = Math.max(1, Math.ceil(moduleAccessTotal / moduleAccessLimit));
  const paginatedModuleAccess = useMemo(() => {
    const start = (moduleAccessPage - 1) * moduleAccessLimit;
    return filteredModuleAccess.slice(start, start + moduleAccessLimit);
  }, [filteredModuleAccess, moduleAccessPage, moduleAccessLimit]);

  // Reset page when search changes
  useEffect(() => {
    setModuleAccessPage(1);
  }, [moduleAccessSearch]);

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate("/recruitment")}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to Recruitment</span>
      </button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings size={28} className="text-gray-900" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Recruitment Settings
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            Manage organisations and offer templates
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto mb-2">
          <TabsTrigger
            value="organisations"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Organisations
          </TabsTrigger>
          <TabsTrigger
            value="offer-templates"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Offer Templates
          </TabsTrigger>


          <TabsTrigger
            value="module-access"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Module Access
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organisations" className="mt-0">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            {/* Header Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Organisations
                </h4>
                <p className="text-gray-500 text-sm font-medium">
                  Manage company organisations for recruitment
                </p>
              </div>
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold shrink-0"
                onClick={openAddOrgDialog}
              >
                <Plus size={16} />
                <span>Add Organisation</span>
              </Button>
            </div>

            {organisationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : organisations.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm font-medium text-gray-500">
                  No organisations yet. Add your first organisation to get started.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {organisations.map((org) => (
                  <div
                    key={org.id}
                    className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {org.name}
                      </p>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">
                        ABN: {org.abn || "—"}
                      </p>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">
                        {org.address || "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => openEditOrgDialog(org)}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setOrgToDelete(org);
                          setDeleteModalOpen(true);
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="offer-templates" className="mt-0">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Offer Templates
                </h4>
                <p className="text-gray-500 text-sm font-medium">
                  Manage main employment templates and custom offer letter templates
                </p>
              </div>
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold shrink-0"
                onClick={() => navigate("/recruitment/settings/offer-templates/new")}
              >
                <Plus size={16} />
                <span>New Template</span>
              </Button>
            </div>

            {templatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-8">
                {/* Main Templates Section */}
                <div className="space-y-3">
                  <h5 className="text-sm font-bold text-gray-400 uppercase tracking-widest">
                    Main Employment Templates
                  </h5>
                  {systemTemplates.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50/50 rounded-xl border border-gray-100">
                      <p className="text-sm font-medium text-gray-500">
                        No main templates available.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {systemTemplates.map((template) => (
                        <div
                          key={template.id}
                          className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-gray-900 truncate">
                                {template.name}
                              </p>
                              <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-700">
                                System
                              </span>
                              {template.isDefault && (
                                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">
                                  Default
                                </span>
                              )}
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${template.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                                {template.isActive ? "Active" : "Inactive"}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 font-medium mt-0.5 truncate">
                              {template.subject}
                            </p>
                            <p className="text-xs text-gray-400 font-medium mt-0.5">
                              {template.templateType === "CASUAL" ? "Casual Employment" : template.templateType === "PERMANENT" ? "Permanent Employment" : "System Template"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => navigate(`/recruitment/settings/offer-templates/${template.id}/edit`)}
                              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                              title="Edit template"
                            >
                              <Pencil size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Custom Templates Section */}
                <div className="space-y-3">
                  <h5 className="text-sm font-bold text-gray-400 uppercase tracking-widest">
                    Custom Templates
                  </h5>
                  {customTemplates.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50/50 rounded-xl border border-gray-100">
                      <p className="text-sm font-medium text-gray-500">
                        No custom templates yet. Create your first custom template to get started.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {customTemplates.map((template) => (
                        <div
                          key={template.id}
                          className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-gray-900 truncate">
                                {template.name}
                              </p>
                              {template.isDefault && (
                                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">
                                  Default
                                </span>
                              )}
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${template.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                                {template.isActive ? "Active" : "Inactive"}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 font-medium mt-0.5 truncate">
                              {template.subject}
                            </p>
                            <p className="text-xs text-gray-400 font-medium mt-0.5">
                              {(template.customFields?.length || 0)} custom field{template.customFields?.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => navigate(`/recruitment/settings/offer-templates/${template.id}/edit`)}
                              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                              title="Edit template"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => openDeleteModal(template)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                              title="Delete template"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="module-access" className="mt-0">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Module Access
                </h4>
                <p className="text-gray-500 text-sm font-medium">
                  Control which users can be invited to view requisitions
                </p>
              </div>
            </div>

            {/* Search */}
            <div className="relative w-full sm:max-w-sm">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <Input
                placeholder="Search users by name or email..."
                className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium"
                value={moduleAccessSearch}
                onChange={(e) => setModuleAccessSearch(e.target.value)}
              />
            </div>

            {moduleAccessLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
                <span className="ml-2 text-sm font-medium text-gray-500">Loading module access...</span>
              </div>
            ) : (
              <div className="overflow-x-auto no-scrollbar">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">
                        User
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Role
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">
                        Access
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedModuleAccess.map((item) => (
                      <tr
                        key={item.user.id}
                        className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-sm font-bold">
                              {item.user.name?.split(" ").map((n) => n[0]).join("")}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-900">
                                {item.user.name}
                              </p>
                              <p className="text-xs text-gray-500 font-medium">{item.user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-gray-600">{item.user.userRole}</span>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={item.accessLevel}
                            disabled={!item.isConfigurable || updatingAccessUserId === item.user.id || !canManageModuleAccess}
                            onChange={(e) => updateModuleAccess(item.user.id, e.target.value)}
                            className="h-9 rounded-lg border border-gray-200 bg-white text-sm font-medium px-3 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <option value="NO_ACCESS">No Access</option>
                            <option value="VIEWER">Viewer</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {moduleAccessTotal > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-100 mt-2">
                    <p className="text-sm text-gray-500 font-medium">
                      Showing{" "}
                      <span className="font-bold text-gray-900">
                        {(moduleAccessPage - 1) * moduleAccessLimit + 1}
                      </span>{" "}
                      -{" "}
                      <span className="font-bold text-gray-900">
                        {Math.min(moduleAccessPage * moduleAccessLimit, moduleAccessTotal)}
                      </span>{" "}
                      of <span className="font-bold text-gray-900">{moduleAccessTotal}</span> users
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 px-3 rounded-lg border-gray-200 bg-white hover:bg-gray-50 text-sm font-bold"
                        onClick={() => setModuleAccessPage((p) => Math.max(1, p - 1))}
                        disabled={moduleAccessPage <= 1}
                      >
                        <ChevronLeft size={16} className="mr-1" />
                        Previous
                      </Button>
                      <span className="text-sm font-bold text-gray-900 px-3 py-1.5 bg-gray-50 rounded-lg min-w-[2.5rem] text-center">
                        {moduleAccessPage}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 px-3 rounded-lg border-gray-200 bg-white hover:bg-gray-50 text-sm font-bold"
                        onClick={() => setModuleAccessPage((p) => Math.min(moduleAccessPages, p + 1))}
                        disabled={moduleAccessPage >= moduleAccessPages}
                      >
                        Next
                        <ChevronRight size={16} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!moduleAccessLoading && paginatedModuleAccess.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm font-medium text-gray-500">
                  {moduleAccessSearch.trim()
                    ? "No users match your search."
                    : "No users found."}
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Modal — Offer Template */}
      <AlertDialog open={deleteModalOpen && !!templateToDelete} onOpenChange={(open) => { if (!open) { setDeleteModalOpen(false); setTemplateToDelete(null); } }}>
        <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <AlertDialogHeader className="p-6 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-500" />
              <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Template</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
              Are you sure you want to delete <span className="font-bold text-gray-700">"{templateToDelete?.name}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 pb-6 gap-2">
            <AlertDialogCancel
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              disabled={deleteLoading}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={handleDeleteTemplate}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Delete Template"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Organisation Dialog */}
      <Dialog open={showOrgDialog} onOpenChange={setShowOrgDialog}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">
              {editingOrg ? "Edit Organisation" : "Add Organisation"}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="e.g., Idarah Pty Ltd"
                className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">
                ABN Number <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="e.g., 12 345 678 901"
                className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                value={orgAbn}
                onChange={(e) => setOrgAbn(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">
                Address <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder="Enter the organisation address..."
                className="bg-gray-50 border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-100 min-h-[80px] resize-y"
                value={orgAddress}
                onChange={(e) => setOrgAddress(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                onClick={() => setShowOrgDialog(false)}
                disabled={orgSubmitting}
              >
                Cancel
              </Button>
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5"
                onClick={handleSaveOrganisation}
                disabled={orgSubmitting}
              >
                {orgSubmitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : editingOrg ? (
                  "Save Changes"
                ) : (
                  "Add Organisation"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal — Organisation */}
      <AlertDialog open={deleteModalOpen && !!orgToDelete} onOpenChange={(open) => { if (!open) { setDeleteModalOpen(false); setOrgToDelete(null); } }}>
        <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <AlertDialogHeader className="p-6 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-500" />
              <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Organisation</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
              Are you sure you want to delete <span className="font-bold text-gray-700">"{orgToDelete?.name}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 pb-6 gap-2">
            <AlertDialogCancel
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              disabled={orgDeleteLoading}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={handleDeleteOrg}
              disabled={orgDeleteLoading}
            >
              {orgDeleteLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Delete Organisation"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default RecruitmentSettings;
